import { panelStorageKey } from '../store/namespace';
import { PANEL_EVENTS } from './protocol';
import React, {
    useRef,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../primitives/Tabs';
import {
    AlignEndVertical,
    AlignHorizontalJustifyCenter,
    AlignStartHorizontal,
    AlignStartVertical,
    AlignEndHorizontal,
    AlignVerticalJustifyCenter,
    X,
    X as CloseIcon,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ChevronDown,
    LayoutPanelLeft,
    Check,
} from '../primitives/icons';
import { useSidePanel } from './useSidePanel';
import { isPanelHidden, isStaleHidden } from './panelVisibilityFlags';
import { FloatingPanel } from './FloatingPanel';
import { PanelGroup, FloatingWindow, TabConfig } from './types';
import { useContextMenu } from '../primitives/ContextMenu';
import { usePanelLabel } from '../primitives/labels';
import {
    useIconAlignmentStore,
    type IconAlignment,
} from '../store/useIconAlignmentStore';
import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from '../primitives/Tooltip';
import { MinimizedSidePanel } from './MinimizedSidePanel';
import { RegularSidePanel } from './RegularSidePanel';
import { generateUniqueId } from './utils';
import { usePanelDrag } from './panelDragUtils';
import { usePanelAutoCollapse } from './usePanelAutoCollapse';
import type { DockTarget } from '../store/useDockLayoutStore';
import '../theme/side-panel.css';
function getCssVarPx(name: string, fallback: number): number {
    if (typeof window !== 'undefined') {
        const val = getComputedStyle(document.documentElement).getPropertyValue(
            name,
        );
        if (val) {
            const num = parseFloat(val);
            if (!isNaN(num)) return num;
        }
    }
    return fallback;
}
const PANEL_CSS_VARS = [
    ['MIN_WIDTH', '--panel-min-width', 90],
    ['MIN_HEIGHT', '--panel-min-height', 90],
    ['DEFAULT_WIDTH', '--panel-default-width', 280],
    ['DEFAULT_HEIGHT', '--panel-default-height', 200],
    ['TABMINWIDTHTHRESHOLD', '--panel-tab-min-width-threshold', 55],
    ['COLLAPSED_WIDTH', '--panel-collapsed-width', 40],
    ['COLLAPSED_HEIGHT', '--panel-collapsed-height', 50],
    ['FLOAT_WINDOW_MIN_WIDTH', '--panel-float-window-min-width', 250],
    ['FLOAT_WINDOW_MIN_HEIGHT', '--panel-float-window-min-height', 180],
    ['TABSCROLLSPEEDRATIO', '--panel-tab-scroll-speed-ratio', 0.2],
    ['GROUP_HEADER_HEIGHT', '--panel-group-header-height', 36],
] as const;
type CssVarsType = {
    [K in (typeof PANEL_CSS_VARS)[number][0]]: number;
};
function getAllPanelCssVars(): CssVarsType {
    const result: Partial<CssVarsType> = {};
    for (const [key, cssVar, fallback] of PANEL_CSS_VARS) {
        result[key as keyof CssVarsType] = getCssVarPx(cssVar, fallback);
    }
    return result as CssVarsType;
}
interface DropZoneProps {
    position: 'before' | 'after';
    groupId: string;
    isOver: boolean;
    /** Marks the column's after-last-group zone: it overlays the panel's
        bottom edge (no flow height) so the last group ends flush. */
    trailing?: boolean;
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
    onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}
const DropZone: React.FC<DropZoneProps> = ({
    position,
    groupId,
    isOver,
    trailing,
    onDragOver,
    onDragLeave,
    onDrop,
}) => {
    return (
        <div
            className={`group-drop-zone ${position} ${isOver ? 'active' : ''}${trailing ? ' trailing' : ''}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            data-before-group={position === 'before' ? groupId : null}
            data-after-group={position === 'after' ? groupId : null}
        />
    );
};
/** Per-instance keys: several permanent shells (right column + bottom dock)
 * must not overwrite each other's collapsed state. The primary instance
 * keeps the legacy unprefixed key so persisted layouts survive. */
const panelCollapsedKey = (panelId: string) =>
    panelId !== 'main-panel'
        ? panelStorageKey(`sidePanel.collapsed.${panelId}`)
        : panelStorageKey('sidePanel.collapsed');
const panelWidthKey = () => panelStorageKey('sidePanel.width');
export interface SidePanelCoreProps {
    panelId: string;
    width?: number;
    panelGroups?: PanelGroup[];
    minWidth?: number;
    defaultWidth?: number;
    minHeight?: number;
    defaultHeight?: number;
    collapsedWidth?: number;
    collapsedHeight?: number;
    floatWindowMinWidth?: number;
    floatWindowMinHeight?: number;
    tabMinWidthThreshold?: number;
    panelPosition: 'left' | 'right' | 'top' | 'bottom';
    /** Position of this instance inside its side's dock strip (App computes
        it from useDockLayoutStore); exposed as data-dock-index for the shared
        dock snap resolver. Defaults to 0. */
    dockIndex?: number;
    setPanelPosition: (pos: 'left' | 'right' | 'top' | 'bottom') => void;
    /** Dock-layout aware drop: side + insert index into that edge's strip
        (shared registry also hosting the toolbar). When provided, wins over
        setPanelPosition. */
    onDockTarget?: (target: DockTarget) => void;
    onAddPanel?: (
        position: 'left' | 'right' | 'top' | 'bottom',
        panelId?: string,
        tab?: TabConfig,
    ) => string;
    onRemovePanel?: (panelId: string) => void;
    floatingWindows?: FloatingWindow[];
    setFloatingWindows?: React.Dispatch<React.SetStateAction<FloatingWindow[]>>;
    setPanelGroups?: React.Dispatch<React.SetStateAction<PanelGroup[]>>;
}
export const SidePanelCore: React.FC<SidePanelCoreProps> = ({
    panelId,
    width: initialWidth,
    panelGroups: externalPanelGroups,
    minWidth,
    defaultWidth,
    minHeight,
    defaultHeight,
    collapsedWidth,
    collapsedHeight,
    floatWindowMinWidth,
    floatWindowMinHeight,
    tabMinWidthThreshold,
    panelPosition,
    dockIndex = 0,
    setPanelPosition,
    onDockTarget,
    onAddPanel,
    onRemovePanel,
    floatingWindows: initialFloatingWindows = [],
    setFloatingWindows: setFloatingWindowsExternal,
    setPanelGroups: setPanelGroupsExternal,
}) => {
    const [cssVars, setCssVars] = useState<CssVarsType>(getAllPanelCssVars());
    useEffect(() => {
        function updateVars() {
            setCssVars(getAllPanelCssVars());
        }
        updateVars();
        window.addEventListener('resize', updateVars);
        return () => window.removeEventListener('resize', updateVars);
    }, []);
    const MIN_WIDTH = cssVars.MIN_WIDTH;
    const MIN_HEIGHT = cssVars.MIN_HEIGHT;
    const DEFAULT_WIDTH = cssVars.DEFAULT_WIDTH;
    const DEFAULT_HEIGHT = cssVars.DEFAULT_HEIGHT;
    const TABMINWIDTHTHRESHOLD = cssVars.TABMINWIDTHTHRESHOLD;
    const COLLAPSED_WIDTH = cssVars.COLLAPSED_WIDTH;
    const COLLAPSED_HEIGHT = cssVars.COLLAPSED_HEIGHT;
    const FLOAT_WINDOW_MIN_WIDTH = cssVars.FLOAT_WINDOW_MIN_WIDTH;
    const FLOAT_WINDOW_MIN_HEIGHT = cssVars.FLOAT_WINDOW_MIN_HEIGHT;
    const TABSCROLLSPEEDRATIO = cssVars.TABSCROLLSPEEDRATIO;
    const GROUP_HEADER_HEIGHT = cssVars.GROUP_HEADER_HEIGHT;
    initialWidth = initialWidth ?? DEFAULT_WIDTH;
    minWidth = minWidth ?? MIN_WIDTH;
    defaultWidth = defaultWidth ?? DEFAULT_WIDTH;
    minHeight = minHeight ?? MIN_HEIGHT;
    defaultHeight = defaultHeight ?? DEFAULT_HEIGHT;
    collapsedWidth = collapsedWidth ?? COLLAPSED_WIDTH;
    collapsedHeight = collapsedHeight ?? COLLAPSED_HEIGHT;
    floatWindowMinWidth = floatWindowMinWidth ?? FLOAT_WINDOW_MIN_WIDTH;
    floatWindowMinHeight = floatWindowMinHeight ?? FLOAT_WINDOW_MIN_HEIGHT;
    tabMinWidthThreshold = tabMinWidthThreshold ?? TABMINWIDTHTHRESHOLD;
    const [hydrated, setHydrated] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const tabsListRef = useRef<HTMLDivElement>(null);
    const { showMenu } = useContextMenu();
    const t = usePanelLabel();
    // Icon alignment (right-click menu): per-panel persisted setting that
    // controls which rail edge the minimized icon stack packs toward
    const iconAlignment = useIconAlignmentStore((s) =>
        s.getIconAlignment(panelId),
    );
    const setIconAlignment = useIconAlignmentStore((s) => s.setIconAlignment);

    /** Right-click menu on the panel shell: icon alignment picker. Only
        offered while the panel is in icon mode (minimized) — regular panels
        have no icon rail to align. Rendered through the app-wide
        ContextMenuProvider; re-opens itself with the fresh checkmark after
        each selection. Labels are orientation-aware: a vertical panel offers
        Top/Center/Bottom, a horizontal panel offers Left/Center/Right. */
    const showIconAlignmentMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!isMinimized) return;
        const isHorizontalPanel =
            panelPosition === 'top' || panelPosition === 'bottom';
        const options: Array<{
            value: IconAlignment;
            label: string;
            Icon: React.ComponentType<{ size?: number | string }>;
        }> = isHorizontalPanel
            ? [
                  {
                      value: 'start',
                      label: 'Left',
                      Icon: AlignStartHorizontal,
                  },
                  {
                      value: 'center',
                      label: 'Center',
                      Icon: AlignHorizontalJustifyCenter,
                  },
                  { value: 'end', label: 'Right', Icon: AlignEndHorizontal },
              ]
            : [
                  { value: 'start', label: 'Top', Icon: AlignStartVertical },
                  {
                      value: 'center',
                      label: 'Center',
                      Icon: AlignVerticalJustifyCenter,
                  },
                  { value: 'end', label: 'Bottom', Icon: AlignEndVertical },
              ];
        const buildMenu = (selected: IconAlignment) => (
            <div
                role="menu"
                aria-label={t('toolbar.alignmentHeading')}
                className="flex min-w-[200px] max-w-[280px] flex-col rounded-md border border-[var(--border)] bg-[var(--popover)] py-1 text-[var(--popover-foreground)] shadow-lg select-none"
            >
                <div
                    className="px-2 pt-1 pb-[2px] text-[10px] uppercase tracking-wide text-[var(--dimmed-foreground)]"
                    aria-hidden="true"
                >
                    {t('toolbar.alignmentHeading')}
                </div>
                {options.map(({ value, label, Icon }) => (
                    <button
                        key={value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={selected === value}
                        className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap px-3 py-[3px] text-left hover:bg-[var(--highlight-color)]"
                        onClick={() => {
                            setIconAlignment(panelId, value);
                            showMenu(buildMenu(value), e.clientX, e.clientY);
                        }}
                    >
                        <Icon size={14} />
                        <span className="flex-1">
                            {t(`toolbar.align.${isHorizontalPanel ? 'h' : 'v'}.${value}`,
                                label,
                            )}
                        </span>
                        <Check
                            size={14}
                            className={`flex-shrink-0 ${
                                selected === value ? 'opacity-100' : 'opacity-0'
                            }`}
                            strokeWidth={2.5}
                        />
                    </button>
                ))}
            </div>
        );
        showMenu(buildMenu(iconAlignment), e.clientX, e.clientY);
    };
    const [showPanel, setShowPanel] = useState(true);
    const [popupTab, setPopupTab] = useState<{
        tabId: string;
        groupId: string;
    } | null>(null);
    const [popupPosition, setPopupPosition] = useState<{
        left: number;
        top: number;
    } | null>(null);
    const [popupWidth, setPopupWidth] = useState(220);
    const [popupHeights, setPopupHeights] = useState<Record<string, number>>(
        {},
    );
    const [popupResizeHandle, setPopupResizeHandle] = useState<string | null>(
        null,
    );
    const [containerHeight, setContainerHeight] = useState(0);
    const [tabDropPosition, setTabDropPosition] = useState<
        Record<string, 'left' | 'right' | null>
    >({});
    const [menuBarHeight, setMenuBarHeight] = useState(0);
    const [miniIsDraggingPanel, setMiniIsDraggingPanel] = useState(false);
    const [miniDragOffset, setMiniDragOffset] = useState<{
        x: number;
        y: number;
    } | null>(null);
    const [miniDragPanelSize, setMiniDragPanelSize] = useState<{
        width: number;
        height: number;
    } | null>(null);
    const [miniDragPos, setMiniDragPos] = useState<{
        x: number;
        y: number;
    } | null>(null);
    const [miniSnapTarget, setMiniSnapTarget] = useState<
        'left' | 'right' | 'top' | 'bottom' | null
    >(null);
    const [tabDragGhost, setTabDragGhost] = useState<{
        content: React.ReactNode;
        x: number;
        y: number;
        width: number;
        height: number;
        icon?: React.ReactNode;
    } | null>(null);
    const [tabDragState, setTabDragState] = useState<{
        dragging: boolean;
        tabId: string;
        groupId: string;
        panelId: string;
        dragElement: HTMLElement | null;
        startX: number;
        startY: number;
        ghostX: number;
        ghostY: number;
        ghostWidth: number;
        ghostHeight: number;
    } | null>(null);
    const [floatingDragState, setFloatingDragState] = useState<{
        dragging: boolean;
        windowId: string;
        tab: TabConfig;
        startX: number;
        startY: number;
        dragElement: HTMLElement | null;
        ghostX: number;
        ghostY: number;
        ghostWidth: number;
        ghostHeight: number;
    } | null>(null);
    const [floatingDragGhost, setFloatingDragGhost] = useState<{
        content: React.ReactNode;
        x: number;
        y: number;
        width: number;
        height: number;
    } | null>(null);
    const [panelDragGhostHtml, setPanelDragGhostHtml] = useState<string | null>(
        null,
    );
    const {
        width,
        height,
        isCollapsed,
        activeTab,
        isResizing,
        activeDropZone,
        floatingWindows,
        panelGroups,
        toggleCollapse,
        createPanelResizeHandleMouseDown,
        createGroupResizeHandleMouseDown,
        handleCloseTab,
        closeFloatingWindow,
        moveFloatingWindow,
        resizeFloatingWindow,
        setFloatingWindowDragging,
        setFloatingWindowResizing,
        handleDragStart,
        handleDragEnd,
        handleDragOver,
        handleDragLeave,
        handleDropZoneDragOver,
        handleDropZoneDragLeave,
        handleDrop,
        handleDropOnList,
        handleDropOnZone,
        setActiveTab,
        isDraggingOutside,
        draggedTab,
        setIsCollapsed,
        setFloatingWindows,
        setPanelGroups,
        setIsDraggingOutside,
        setDraggedTab,
        toggleGroupMinimize,
        handleTabDrop,
        isMinimized,
        toggleMinimized,
        setHeight,
        hydrating,
    } = useSidePanel({
        panelId,
        initialWidth,
        initialPanelGroups: externalPanelGroups || [],
        minWidth,
        defaultWidth,
        minHeight,
        defaultHeight,
        collapsedWidth,
        panelPosition,
        setPanelGroups: setPanelGroupsExternal,
        onRemovePanel,
        floatingWindows: initialFloatingWindows,
        setFloatingWindows: setFloatingWindowsExternal,
    });
    const {
        isDraggingPanel,
        setIsDraggingPanel,
        dragOffset,
        setDragOffset,
        dragPanelSize,
        setDragPanelSize,
        dragPos,
        setDragPos,
        snapTarget,
        setSnapTarget,
        handlePanelDragStart,
        snapOverlay,
    } = usePanelDrag({
        panelRef,
        initialPanelPosition: panelPosition,
        panelId,
        onSnap: setPanelPosition,
        onDockTarget: onDockTarget,
    });
    const visibleGroups = useMemo(
        () => panelGroups.filter((g) => g.isVisible),
        [panelGroups],
    );
    const isHorizontal = panelPosition === 'top' || panelPosition === 'bottom';
    // Photoshop-style auto-collapse: only for vertical (left/right) columns
    // where stacked groups share the panel's height. The auto state lives on
    // the same isMinimized flag as the manual toggle (header button / resize
    // edge click).
    const autoCollapse = usePanelAutoCollapse({
        enabled: !isHorizontal && !isMinimized && hydrated,
        groups: visibleGroups,
        setPanelGroups: setPanelGroups as React.Dispatch<
            React.SetStateAction<PanelGroup[]>
        >,
        activeTab,
        minHeight,
        defaultHeight,
    });
    useEffect(() => {
        setHydrated(true);
    }, []);
    useEffect(() => {
        try {
            const storedCollapsed = localStorage.getItem(panelCollapsedKey(panelId));
            if (storedCollapsed !== null) {
                setIsCollapsed(storedCollapsed === 'true');
            }
        } catch {}
    }, []);
    useEffect(() => {
        try {
            localStorage.setItem(panelCollapsedKey(panelId), String(isCollapsed));
        } catch {}
    }, [isCollapsed, panelId]);
    useEffect(() => {
        try {
            // Width only describes vertical (left/right) shells; a horizontal
            // panel's width is owned by the dock strip and must never
            // overwrite the vertical shell's persisted width.
            if (
                !isCollapsed &&
                (panelPosition === 'left' || panelPosition === 'right')
            ) {
                localStorage.setItem(panelWidthKey(), String(width));
            }
        } catch {}
    }, [width, isCollapsed, panelPosition]);
    useEffect(() => {
        if (isMinimized) {
            setShowPanel(true);
        }
    }, [isMinimized]);

    useEffect(() => {
        const handleTogglePanel = (event: Event) => {
            // Skip panels that were just hidden by this same toggle cycle,
            // otherwise the panel shell is re-shown right after being closed.
            const detail = (event as CustomEvent<{ panelId?: string }>).detail;
            if (
                detail?.panelId &&
                isPanelHidden(detail.panelId) &&
                !isStaleHidden(detail.panelId)
            )
                return;
            setShowPanel(true);
        };
        window.addEventListener(PANEL_EVENTS.togglePanel, handleTogglePanel);
        return () =>
            window.removeEventListener(PANEL_EVENTS.togglePanel, handleTogglePanel);
    }, []);
    const [horizontalGroupWidths, setHorizontalGroupWidths] = useState<
        number[]
    >([]);
    useEffect(() => {
        const menuBar = document.querySelector('.menubar, #menubar');
        const optionsBar = document.querySelector('.options-bar, #options-bar');
        const menuBarHeight = menuBar
            ? (menuBar as HTMLElement).offsetHeight
            : 40;
        const optionsBarHeight = optionsBar
            ? (optionsBar as HTMLElement).offsetHeight
            : 0;
        const totalHeaderHeight = menuBarHeight + optionsBarHeight;
        document.documentElement.style.setProperty(
            '--total-header-height',
            `${totalHeaderHeight}px`,
        );
        setMenuBarHeight(totalHeaderHeight);
    }, []);
    useEffect(() => {
        if (isHorizontal && visibleGroups.length > 0) {
            setHorizontalGroupWidths((prev) =>
                prev.length === visibleGroups.length
                    ? prev
                    : Array(visibleGroups.length).fill(
                          1 / visibleGroups.length,
                      ),
            );
        }
    }, [isHorizontal, visibleGroups.length]);
    useEffect(() => {
        function updateHeight() {
            if (panelRef.current)
                setContainerHeight(panelRef.current.offsetHeight);
        }
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);
    // Horizontal group resize: mousedown handler (zero React re-renders during drag)
    // widthsRef holds the live fractions; DOM elements are updated directly.
    const widthsRef = useRef<number[]>([]);
    // Keep widthsRef in sync with state (only changes outside of a drag)
    useEffect(() => {
        widthsRef.current = horizontalGroupWidths;
    }, [horizontalGroupWidths]);

    const handleHorizontalGroupResizeMouseDown = useCallback(
        (index: number) => (e: React.MouseEvent) => {
            e.preventDefault();
            document.body.classList.add('panel-resizing');
            const containerEl = panelRef.current;
            if (!containerEl) return;
            const containerWidth = containerEl.offsetWidth;
            let lastX = e.clientX;
            let rafId: number | null = null;
            let currentX = lastX;
            const minW = 120;

            // Grab DOM elements at drag-start; avoid querying every frame
            const groupEls = Array.from(
                containerEl.querySelectorAll<HTMLElement>(
                    '.panel-group.horizontal',
                ),
            );

            const onMouseMove = (ev: MouseEvent) => {
                currentX = ev.clientX;
                if (rafId === null) {
                    rafId = requestAnimationFrame(() => {
                        rafId = null;
                        const deltaX = currentX - lastX;
                        lastX = currentX;
                        const widths = widthsRef.current;
                        const w0 = widths[index] * containerWidth;
                        const w1 = widths[index + 1] * containerWidth;
                        let newW0 = w0 + deltaX;
                        let newW1 = w1 - deltaX;
                        if (newW0 < minW) {
                            newW1 -= minW - newW0;
                            newW0 = minW;
                        }
                        if (newW1 < minW) {
                            newW0 -= minW - newW1;
                            newW1 = minW;
                        }
                        if (newW0 < minW || newW1 < minW) return;
                        const next = [...widths];
                        next[index] = newW0 / containerWidth;
                        next[index + 1] = newW1 / containerWidth;
                        const sum = next.reduce((a, b) => a + b, 0);
                        const normalized = next.map((w) => w / sum);
                        widthsRef.current = normalized;
                        // DOM-only updates: zero React re-renders
                        normalized.forEach((w, i) => {
                            if (groupEls[i]) {
                                groupEls[i].style.width =
                                    `calc(${w * 100}% - 2px)`;
                            }
                        });
                    });
                }
            };

            const onMouseUp = () => {
                if (rafId !== null) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
                document.body.classList.remove('panel-resizing');
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
                // Clear inline styles and commit to React state once
                groupEls.forEach((el) => {
                    el.style.width = '';
                });
                setHorizontalGroupWidths([...widthsRef.current]);
            };

            window.addEventListener('mousemove', onMouseMove, {
                passive: true,
            });
            window.addEventListener('mouseup', onMouseUp);
        },
        [panelRef],
    );
    const getPanelGroupStyle = (group: PanelGroup, index: number) => {
        if (isHorizontal) {
            return {
                width: `calc(${(horizontalGroupWidths[index] || 1 / visibleGroups.length) * 100}% - 2px)`,
            };
        }
        if (autoCollapse.isGroupCollapsed(group.id)) {
            return { height: `${GROUP_HEADER_HEIGHT}px` };
        }
        // Last open group absorbs rounding drift from applyProportions so
        // the column always sits flush against the panel's bottom edge.
        const h = group.height || MIN_HEIGHT;
        const isLastOpen =
            index === visibleGroups.length - 1 && !group.isMinimized;
        return isLastOpen
            ? { height: `${h}px`, flexGrow: 1 }
            : { height: `${h}px` };
    };
    useEffect(() => {
        document.documentElement.style.setProperty(
            '--panel-min-height',
            `${minHeight}px`,
        );
        document.documentElement.style.setProperty(
            '--panel-default-width',
            `${defaultWidth}px`,
        );
        document.documentElement.style.setProperty(
            '--panel-collapsed-width',
            `${collapsedWidth}px`,
        );
        document.documentElement.style.setProperty(
            '--panel-horizontal-height',
            `${defaultHeight}px`,
        );
    }, [minHeight, defaultWidth, collapsedWidth, defaultHeight]);
    useEffect(() => {
        if (!tabDragState) return;
        let isDragging = tabDragState.dragging;
        let lastDropTarget: HTMLElement | null = null;
        let lastDropZone: HTMLElement | null = null;
        let lastDropPosition: 'left' | 'right' | null = null;
        const moveListener = (e: MouseEvent) => {
            if (!tabDragState) return;
            if (!isDragging) {
                const dx = e.clientX - tabDragState.startX;
                const dy = e.clientY - tabDragState.startY;
                if (Math.sqrt(dx * dx + dy * dy) > 5) {
                    isDragging = true;
                    tabDragState.dragElement?.classList.add('dragging');
                    document.body.classList.add(
                        'drag-in-progress',
                        'dragging-tab',
                    );
                } else {
                    return;
                }
            }
            setTabDragGhost({
                content: tabDragState.dragElement?.innerHTML ? (
                    <div
                        className="panel-tab dragging"
                        dangerouslySetInnerHTML={{
                            __html: tabDragState.dragElement.innerHTML,
                        }}
                    />
                ) : null,
                x: e.clientX,
                y: e.clientY,
                width: tabDragState.ghostWidth,
                height: tabDragState.ghostHeight,
                icon: panelGroups
                    .find((g) => g.id === tabDragState.groupId)
                    ?.tabs.find((t) => t.id === tabDragState.tabId)?.icon,
            });
            const elements = document.elementsFromPoint(e.clientX, e.clientY);
            const tabTarget = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('panel-tab') &&
                    el !== tabDragState.dragElement &&
                    !el.classList.contains('dragging'),
            ) as HTMLElement | null;
            const dropZoneTarget = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('group-drop-zone'),
            ) as HTMLElement | null;
            if (lastDropTarget && lastDropTarget !== tabTarget) {
                lastDropTarget.classList.remove(
                    'drop-target-left',
                    'drop-target-right',
                );
                lastDropTarget = null;
                lastDropPosition = null;
            }
            if (lastDropZone && lastDropZone !== dropZoneTarget) {
                lastDropZone.classList.remove('active');
                lastDropZone = null;
            }
            if (tabTarget) {
                if (lastDropZone) {
                    lastDropZone.classList.remove('active');
                    lastDropZone = null;
                }
                const tabRect = tabTarget.getBoundingClientRect();
                const pos =
                    e.clientX < tabRect.left + tabRect.width / 2
                        ? 'left'
                        : 'right';
                if (pos !== lastDropPosition || lastDropTarget !== tabTarget) {
                    if (lastDropTarget && lastDropTarget !== tabTarget) {
                        lastDropTarget.classList.remove(
                            'drop-target-left',
                            'drop-target-right',
                        );
                    }
                    tabTarget.classList.remove(
                        'drop-target-left',
                        'drop-target-right',
                    );
                    tabTarget.classList.add(`drop-target-${pos}`);
                    lastDropPosition = pos;
                }
                lastDropTarget = tabTarget;
                setTabDropPosition((prev) => ({
                    ...prev,
                    [tabTarget.getAttribute('data-tab-id') || '']: pos,
                }));
                setIsDraggingOutside(false);
            } else if (dropZoneTarget) {
                if (lastDropTarget) {
                    lastDropTarget.classList.remove(
                        'drop-target-left',
                        'drop-target-right',
                    );
                    lastDropTarget = null;
                    lastDropPosition = null;
                }
                dropZoneTarget.classList.add('active');
                lastDropZone = dropZoneTarget;
                setIsDraggingOutside(false);
            } else {
                if (lastDropTarget) {
                    lastDropTarget.classList.remove(
                        'drop-target-left',
                        'drop-target-right',
                    );
                    lastDropTarget = null;
                    lastDropPosition = null;
                }
                if (lastDropZone) {
                    lastDropZone.classList.remove('active');
                    lastDropZone = null;
                }
                const allPanels = document.querySelectorAll('.side-panel');
                let isOverAnyPanel = false;
                allPanels.forEach((panel) => {
                    if (!isOverAnyPanel) {
                        const rect = (
                            panel as HTMLElement
                        ).getBoundingClientRect();
                        isOverAnyPanel =
                            e.clientX >= rect.left &&
                            e.clientX <= rect.right &&
                            e.clientY >= rect.top &&
                            e.clientY <= rect.bottom;
                    }
                });
                setIsDraggingOutside(!isOverAnyPanel);
            }
        };
        const upListener = (e: MouseEvent) => {
            if (!tabDragState) return;
            if (!isDragging) {
                cleanup();
                return;
            }
            setTabDragGhost(null);
            if (lastDropTarget) {
                lastDropTarget.classList.remove(
                    'drop-target-left',
                    'drop-target-right',
                );
                lastDropTarget = null;
            }
            if (lastDropZone) {
                lastDropZone.classList.remove('active');
                lastDropZone = null;
            }
            document
                .querySelectorAll(
                    '.panel-tab.drop-target-left, .panel-tab.drop-target-right',
                )
                .forEach((el) => {
                    el.classList.remove(
                        'drop-target-left',
                        'drop-target-right',
                    );
                });
            document
                .querySelectorAll('.group-drop-zone.active')
                .forEach((zone) => {
                    zone.classList.remove('active');
                });
            const elements = document.elementsFromPoint(e.clientX, e.clientY);
            const targetPanelElement = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('side-panel'),
            ) as HTMLElement | null;
            const targetPanelId =
                targetPanelElement?.getAttribute('data-panel-id') || '';
            const tabTarget = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('panel-tab') &&
                    el !== tabDragState?.dragElement &&
                    !el.classList.contains('dragging'),
            ) as HTMLElement | null;
            const dropZoneTarget = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('group-drop-zone'),
            ) as HTMLElement | null;
            let droppedOutside = false;
            if (!tabTarget && !dropZoneTarget && panelRef.current) {
                const rect = panelRef.current.getBoundingClientRect();
                if (
                    e.clientX < rect.left ||
                    e.clientX > rect.right ||
                    e.clientY < rect.top ||
                    e.clientY > rect.bottom
                ) {
                    droppedOutside = true;
                }
            }
            setTabDropPosition({});
            if (
                targetPanelId &&
                targetPanelId !== panelId &&
                tabDragState.panelId === panelId
            ) {
                const tab = panelGroups
                    .find((g) => g.id === tabDragState.groupId)
                    ?.tabs.find((t) => t.id === tabDragState.tabId);
                if (tab) {
                    let targetGroupId = '';
                    let position = 'after';
                    if (tabTarget) {
                        const targetGroupElement =
                            tabTarget.closest('[data-group-id]');
                        if (targetGroupElement instanceof HTMLElement) {
                            targetGroupId =
                                targetGroupElement.getAttribute(
                                    'data-group-id',
                                ) || '';
                        }
                        const tabRect = tabTarget.getBoundingClientRect();
                        position =
                            e.clientX < tabRect.left + tabRect.width / 2
                                ? 'left'
                                : 'right';
                    } else if (dropZoneTarget) {
                        const beforeGroupId =
                            dropZoneTarget.getAttribute('data-before-group');
                        const afterGroupId =
                            dropZoneTarget.getAttribute('data-after-group');
                        targetGroupId = beforeGroupId || afterGroupId || '';
                        position = beforeGroupId ? 'before' : 'after';
                    }
                    const transferEvent = new CustomEvent(
                        PANEL_EVENTS.transferTab,
                        {
                            detail: {
                                tab,
                                sourcePanel: panelId,
                                targetPanel: targetPanelId,
                                targetGroupId,
                                position,
                            },
                        },
                    );
                    window.dispatchEvent(transferEvent);
                    setTimeout(() => {
                        setPanelGroups((prev) => {
                            return prev
                                .map((group) => {
                                    if (group.id === tabDragState.groupId) {
                                        return {
                                            ...group,
                                            tabs: group.tabs.filter(
                                                (t) =>
                                                    t.id !== tabDragState.tabId,
                                            ),
                                        };
                                    }
                                    return group;
                                })
                                .filter((group) => group.tabs.length > 0);
                        });
                    }, 10);
                    cleanup();
                    return;
                }
            }
            if (tabTarget) {
                const targetTabId = tabTarget.getAttribute('data-tab-id') || '';
                const targetGroupElement = tabTarget.closest('[data-group-id]');
                const targetGroupId =
                    targetGroupElement instanceof HTMLElement
                        ? targetGroupElement.dataset.groupId || ''
                        : '';
                const tabRect = tabTarget.getBoundingClientRect();
                const pos =
                    e.clientX < tabRect.left + tabRect.width / 2
                        ? 'left'
                        : 'right';
                setTimeout(() => {
                    setPanelGroups((prev) => {
                        const newGroups = prev.map((group) => ({
                            ...group,
                            tabs: [...group.tabs],
                        }));
                        let sourceGroupIndex = newGroups.findIndex(
                            (g) => g.id === tabDragState.groupId,
                        );
                        let targetGroupIndex = newGroups.findIndex(
                            (g) => g.id === targetGroupId,
                        );
                        if (sourceGroupIndex === -1 || targetGroupIndex === -1)
                            return prev;
                        const sourceGroup = newGroups[sourceGroupIndex];
                        const targetGroup = newGroups[targetGroupIndex];
                        const sourceIndex = sourceGroup.tabs.findIndex(
                            (t) => t.id === tabDragState.tabId,
                        );
                        if (sourceIndex === -1) return prev;
                        let insertIndex = targetGroup.tabs.findIndex(
                            (t) => t.id === targetTabId,
                        );
                        const sameGroup = sourceGroup.id === targetGroup.id;
                        if (sameGroup && targetTabId === tabDragState.tabId) {
                            return prev;
                        }
                        // A const (not the captured `let`) keeps the closure
                        // below narrowed to a defined tab.
                        const sourceTab: TabConfig = {
                            ...sourceGroup.tabs[sourceIndex],
                        };
                        sourceGroup.tabs.splice(sourceIndex, 1);
                        if (sameGroup && sourceIndex < insertIndex) {
                            insertIndex--;
                        }
                        insertIndex = Math.max(0, insertIndex);
                        if (pos === 'left') {
                            targetGroup.tabs.splice(insertIndex, 0, sourceTab);
                        } else {
                            const isLastPosition =
                                insertIndex === targetGroup.tabs.length - 1;
                            targetGroup.tabs.splice(
                                isLastPosition
                                    ? targetGroup.tabs.length
                                    : insertIndex + 1,
                                0,
                                sourceTab,
                            );
                        }
                        setTimeout(() => setActiveTab(sourceTab.id), 0);
                        return newGroups.filter(
                            (group) => group.tabs.length > 0,
                        );
                    });
                }, 0);
            } else if (dropZoneTarget) {
                const beforeGroupId = dropZoneTarget.dataset.beforeGroup;
                const afterGroupId = dropZoneTarget.dataset.afterGroup;
                const zoneId = beforeGroupId
                    ? beforeGroupId
                    : afterGroupId
                      ? afterGroupId
                      : undefined;
                const position = beforeGroupId ? 'before' : 'after';
                if (zoneId) {
                    setTimeout(() => {
                        setPanelGroups((prev) => {
                            const newGroups = prev.map((group) => ({
                                ...group,
                                tabs: [...group.tabs],
                            }));
                            let sourceTab: TabConfig | undefined;
                            const sourceGroup = newGroups.find(
                                (g) => g.id === tabDragState.groupId,
                            );
                            if (sourceGroup) {
                                const sourceIndex = sourceGroup.tabs.findIndex(
                                    (t) => t.id === tabDragState.tabId,
                                );
                                if (sourceIndex !== -1) {
                                    sourceTab = {
                                        ...sourceGroup.tabs[sourceIndex],
                                    };
                                    sourceGroup.tabs = sourceGroup.tabs.filter(
                                        (t) => t.id !== tabDragState.tabId,
                                    );
                                }
                            }
                            if (!sourceTab) return prev;
                            const newGroup: PanelGroup = {
                                id: generateUniqueId(),
                                isVisible: true,
                                height: DEFAULT_HEIGHT,
                                tabs: [sourceTab],
                            };
                            const targetGroupIndex = newGroups.findIndex(
                                (g) => g.id === zoneId,
                            );
                            if (targetGroupIndex === -1) return prev;
                            const insertAtIndex =
                                position === 'before'
                                    ? targetGroupIndex
                                    : targetGroupIndex + 1;
                            newGroups.splice(insertAtIndex, 0, newGroup);
                            // A const (not the captured `let`) keeps the
                            // closure below narrowed to a defined tab.
                            const movedTab = sourceTab;
                            setTimeout(() => setActiveTab(movedTab.id), 0);
                            return newGroups.filter(
                                (group) => group.tabs.length > 0,
                            );
                        });
                    }, 0);
                }
            } else if (droppedOutside) {
                const tabToFloat = panelGroups
                    .find((g) => g.id === tabDragState.groupId)
                    ?.tabs.find((t) => t.id === tabDragState.tabId);
                if (tabToFloat) {
                    setTimeout(() => {
                        setPanelGroups((prev) => {
                            const newGroups = prev
                                .map((group) =>
                                    group.id === tabDragState.groupId
                                        ? {
                                              ...group,
                                              tabs: group.tabs.filter(
                                                  (t) =>
                                                      t.id !==
                                                      tabDragState.tabId,
                                              ),
                                          }
                                        : group,
                                )
                                .filter((group) => group.tabs.length > 0);
                            return newGroups;
                        });
                        const newFloatingWindow: FloatingWindow = {
                            id: `float-${generateUniqueId()}`,
                            tab: { ...tabToFloat },
                            position: { x: e.clientX - 100, y: e.clientY - 30 },
                            size: {
                                width: FLOAT_WINDOW_MIN_WIDTH,
                                height: FLOAT_WINDOW_MIN_HEIGHT,
                            },
                            isResizing: false,
                            isDragging: false,
                        };
                        setTimeout(
                            () =>
                                setFloatingWindows((prev) => [
                                    ...prev,
                                    newFloatingWindow,
                                ]),
                            0,
                        );
                    }, 0);
                }
            }
            cleanup();
        };
        function cleanup() {
            setTabDragGhost(null);
            setTabDragState(null);
            document.body.classList.remove('drag-in-progress', 'dragging-tab');
            if (tabDragState?.dragElement) {
                tabDragState.dragElement.classList.remove('dragging');
            }
            setIsDraggingOutside(false);
            setDraggedTab(null);
            setTabDropPosition({});
            document
                .querySelectorAll(
                    '.panel-tab.drop-target-left, .panel-tab.drop-target-right',
                )
                .forEach((el) => {
                    el.classList.remove(
                        'drop-target-left',
                        'drop-target-right',
                    );
                });
            document
                .querySelectorAll('.group-drop-zone.active')
                .forEach((zone) => {
                    zone.classList.remove('active');
                });
            window.removeEventListener('mousemove', moveListener);
            window.removeEventListener('mouseup', upListener);
        }
        window.addEventListener('mousemove', moveListener);
        window.addEventListener('mouseup', upListener);
        return () => {
            window.removeEventListener('mousemove', moveListener);
            window.removeEventListener('mouseup', upListener);
        };
    }, [
        tabDragState,
        setTabDragGhost,
        setDraggedTab,
        setIsDraggingOutside,
        setTabDropPosition,
        setFloatingWindows,
        setPanelGroups,
        panelGroups,
        FLOAT_WINDOW_MIN_WIDTH,
        FLOAT_WINDOW_MIN_HEIGHT,
        setActiveTab,
        generateUniqueId,
        DEFAULT_HEIGHT,
    ]);
    useEffect(() => {
        function handleFloatingWindowDrop(event: Event) {
            const customEvent = event as CustomEvent;
            const { windowId, tabId, insertIndex, panelId } =
                customEvent.detail || {};
            if (!windowId || !tabId || !panelId) return;
            const floatingWindow = floatingWindows.find(
                (w) => w.id === windowId && w.tab.id === tabId,
            );
            if (!floatingWindow) return;
            setPanelGroups((prev) => {
                let found = false;
                const newGroups = prev.map((group) => {
                    if (group.id === panelId) {
                        found = true;
                        if (group.tabs.some((t) => t.id === tabId))
                            return group;
                        const tabs = [...group.tabs];
                        if (
                            typeof insertIndex === 'number' &&
                            insertIndex >= 0 &&
                            insertIndex <= tabs.length
                        ) {
                            tabs.splice(insertIndex, 0, floatingWindow.tab);
                        } else {
                            tabs.push(floatingWindow.tab);
                        }
                        return { ...group, tabs };
                    }
                    return group;
                });
                if (!found) {
                    const tabs = [floatingWindow.tab];
                    newGroups.push({
                        id: panelId,
                        isVisible: true,
                        isMinimized: true,
                        height: 40,
                        tabs,
                    });
                }
                return newGroups;
            });
            setFloatingWindows((prev) =>
                prev.filter((w) => !(w.id === windowId && w.tab.id === tabId)),
            );
        }
        window.addEventListener(
            PANEL_EVENTS.floatingWindowDrop,
            handleFloatingWindowDrop as EventListener,
        );
        return () => {
            window.removeEventListener(
                PANEL_EVENTS.floatingWindowDrop,
                handleFloatingWindowDrop as EventListener,
            );
        };
    }, [floatingWindows, setPanelGroups, setFloatingWindows]);
    useEffect(() => {
        const handleTabTransfer = (e: Event) => {
            const event = e as CustomEvent;
            const { tab, targetPanel, sourcePanel, targetGroupId, position } =
                event.detail;
            if (targetPanel !== panelId) return;
            if (sourcePanel === panelId) return;
            if (!isMinimized) {
                setTimeout(() => {
                    setPanelGroups((prev) => {
                        const newGroups = prev.map((group) => ({
                            ...group,
                            tabs: [...group.tabs],
                        }));
                        if (
                            (position === 'before' || position === 'after') &&
                            targetGroupId
                        ) {
                            const targetGroupIndex = newGroups.findIndex(
                                (g) => g.id === targetGroupId,
                            );
                            if (targetGroupIndex !== -1) {
                                const insertAtIndex =
                                    position === 'before'
                                        ? targetGroupIndex
                                        : targetGroupIndex + 1;
                                const newGroup: PanelGroup = {
                                    id: generateUniqueId(),
                                    isVisible: true,
                                    height: DEFAULT_HEIGHT,
                                    tabs: [tab],
                                };
                                newGroups.splice(insertAtIndex, 0, newGroup);
                                return newGroups;
                            }
                        } else if (targetGroupId) {
                            const targetGroupIndex = newGroups.findIndex(
                                (g) => g.id === targetGroupId,
                            );
                            if (targetGroupIndex !== -1) {
                                const targetGroup = newGroups[targetGroupIndex];
                                if (position === 'left') {
                                    targetGroup.tabs.unshift(tab);
                                } else {
                                    targetGroup.tabs.push(tab);
                                }
                                return newGroups;
                            }
                        }
                        const newGroup: PanelGroup = {
                            id: generateUniqueId(),
                            isVisible: true,
                            height: DEFAULT_HEIGHT,
                            tabs: [tab],
                        };
                        newGroups.push(newGroup);
                        return newGroups;
                    });
                    setTimeout(() => {
                        setActiveTab(tab.id);
                    }, 0);
                }, 0);
            }
        };
        window.addEventListener(
            PANEL_EVENTS.transferTab,
            handleTabTransfer as EventListener,
        );
        return () => {
            window.removeEventListener(
                PANEL_EVENTS.transferTab,
                handleTabTransfer as EventListener,
            );
        };
    }, [
        panelId,
        setPanelGroups,
        setActiveTab,
        DEFAULT_HEIGHT,
        generateUniqueId,
        isMinimized,
    ]);
    const [removalRequested, setRemovalRequested] = useState(false);
    // Hide the panel shell when every group is hidden (e.g. the last visible
    // panel was closed from the Window menu); re-show it when a group becomes
    // visible again.
    useEffect(() => {
        setShowPanel(visibleGroups.length > 0);
    }, [visibleGroups.length]);
    useEffect(() => {
        if (
            panelGroups.length === 0 &&
            floatingWindows.length === 0 &&
            !removalRequested &&
            panelId !== 'main-panel' &&
            typeof onRemovePanel === 'function'
        ) {
            setRemovalRequested(true);
            // Destroy the shell for real: an unmount (not display:none) keeps
            // the instance's togglePanel/requestRestoreTab listeners from
            // resurrecting panels later, which is what made detached panels
            // come back in pairs. The primary shell only hides — it owns the
            // restore listeners and must stay mounted.
            setTimeout(() => {
                setShowPanel(false);
                onRemovePanel(panelId);
            }, 0);
        }
    }, [
        panelGroups.length,
        floatingWindows.length,
        removalRequested,
        onRemovePanel,
        panelId,
    ]);
    useEffect(() => {
        if (
            (panelGroups.length > 0 || floatingWindows.length > 0) &&
            removalRequested
        ) {
            setRemovalRequested(false);
        }
    }, [panelGroups.length, floatingWindows.length, removalRequested]);
    const handleFloatingPanelTabDragStart = (
        windowId: string,
        tab: TabConfig,
        e: React.MouseEvent,
    ) => {
        if (e.button !== 0) return;
        const dragElement = e.currentTarget as HTMLElement;
        const rect = dragElement.getBoundingClientRect();
        setFloatingDragState({
            dragging: false,
            windowId,
            tab,
            startX: e.clientX,
            startY: e.clientY,
            dragElement,
            ghostX: e.clientX,
            ghostY: e.clientY,
            ghostWidth: rect.width,
            ghostHeight: rect.height,
        });
    };
    const handleTabMouseDown = (
        e: React.MouseEvent,
        tabId: string,
        groupId: string,
    ) => {
        if (e.button !== 0) return;
        const dragElement = e.currentTarget as HTMLElement;
        const rect = dragElement.getBoundingClientRect();
        setTabDragState({
            dragging: false,
            tabId,
            groupId,
            panelId,
            dragElement,
            startX: e.clientX,
            startY: e.clientY,
            ghostX: e.clientX,
            ghostY: e.clientY,
            ghostWidth: rect.width,
            ghostHeight: rect.height,
        });
    };
    const PANEL_LONG_PRESS_TIMEOUT = 300;
    const panelLongPressTimeout = useRef<NodeJS.Timeout | null>(null);
    const panelLongPressTriggered = useRef(false);
    const cancelLongPressRef = useRef<() => void>(() => {});
    const isAnyActionActive = !!(
        isResizing ||
        tabDragState ||
        floatingDragState ||
        isDraggingPanel ||
        (isMinimized && miniIsDraggingPanel)
    );
    const handlePanelLongPressMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (isMinimized) {
            if (
                target.closest('.tab-icon-wrapper') ||
                target.closest('.react-resizable-handle') ||
                target.closest('.sidepanel-popup') ||
                target.closest('.sidepanel-popup-body') ||
                target.closest('.sidepanel-popup-header') ||
                target.closest('.panel-tab-content') ||
                target.closest('.side-panel-header-btn') ||
                target.closest('.side-panel-header-actions')
            ) {
                return;
            }
        } else {
            if (
                target.closest('.panel-content') ||
                target.closest('.panel-tab') ||
                target.closest('.tab-icon-wrapper') ||
                target.closest('.sidepanel-popup-resize-handle') ||
                target.closest('.floating-panel-drag-handle') ||
                target.closest('.panel-tab-content') ||
                target.closest('.side-panel-header-btn') ||
                target.closest('.side-panel-header-actions')
            ) {
                return;
            }
        }
        if (isAnyActionActive) return;
        panelLongPressTriggered.current = false;
        if (panelLongPressTimeout.current)
            clearTimeout(panelLongPressTimeout.current);
        panelLongPressTimeout.current = setTimeout(() => {
            if (isAnyActionActive) return;
            panelLongPressTriggered.current = true;
            if (panelRef.current) {
                setPanelDragGhostHtml(panelRef.current.outerHTML);
            }
            handlePanelDragStart(e);
        }, PANEL_LONG_PRESS_TIMEOUT);
        const cancelLongPress = () => {
            if (panelLongPressTimeout.current) {
                clearTimeout(panelLongPressTimeout.current);
                panelLongPressTimeout.current = null;
            }
        };
        cancelLongPressRef.current = cancelLongPress;
        const handleUp = () => {
            cancelLongPress();
            window.removeEventListener('mouseup', handleUp);
            window.removeEventListener('mouseleave', handleUp);
        };
        window.addEventListener('mouseup', handleUp);
        window.addEventListener('mouseleave', handleUp);
    };
    useEffect(() => {
        if (isAnyActionActive) {
            cancelLongPressRef.current?.();
        }
    }, [isAnyActionActive]);
    useEffect(() => {
        if (!isDraggingPanel && panelDragGhostHtml) {
            setPanelDragGhostHtml(null);
        }
    }, [isDraggingPanel, panelDragGhostHtml]);
    const handleConvertFloatingToSidePanel = (
        windowId: string,
        position: 'left' | 'right' | 'top' | 'bottom',
    ) => {
        try {
            const floatingWindow = floatingWindows.find(
                (w) => w.id === windowId,
            );
            if (!floatingWindow) {
                console.error('Could not find floating window:', windowId);
                return;
            }
            if (!onAddPanel) {
                console.warn(
                    'onAddPanel not provided - cannot convert floating window to panel',
                );
                return;
            }
            const targetPanelId = onAddPanel(
                position,
                undefined,
                floatingWindow.tab,
            );
            closeFloatingWindow(windowId);
            if (setFloatingWindowsExternal) {
                console.log(
                    `Updating external floating windows state to remove: ${windowId}`,
                    { sidepanelType: 'regular' },
                );
                setTimeout(() => {
                    setFloatingWindowsExternal((prev) => {
                        const filtered = prev.filter((w) => w.id !== windowId);
                        console.log(
                            `Filtered external floating windows from ${prev.length} to ${filtered.length}`,
                            { sidepanelType: 'regular' },
                        );
                        return filtered;
                    });
                }, 0);
            } else {
                console.warn(
                    'setFloatingWindowsExternal prop is missing - cannot update parent state',
                );
            }
        } catch (err) {
            console.error('Error when adding panel:', err);
        }
    };

    const checkRemovePanelAfterFloatingClose = () => {
        if (
            panelGroups.length === 0 &&
            floatingWindows.length === 0 &&
            typeof onRemovePanel === 'function'
        ) {
            setRemovalRequested(true);
            setTimeout(() => {
                onRemovePanel(panelId); // Paneli parent'tan da kaldır
            }, 0);
        }
    };

    if (hydrating) return null;
    if (!hydrated) {
        return (
            <div
                className={`side-panel ssr-placeholder${isCollapsed ? ' collapsed' : ''} position-${panelPosition}`}
                style={{
                    width: `${collapsedWidth}px`,
                }}
            />
        );
    }
    // Floating windows are rendered once by App; rendering them here as well
    // duplicates every floating panel per mounted SidePanel instance.
    if (!showPanel) {
        return (
            <div
                className={`side-panel${isMinimized ? ' minimized' : ''} hidden position-${panelPosition}`}
                data-panel-id={panelId}
            />
        );
    }
    let panelStyle: React.CSSProperties;
    let panelClasses = `side-panel${isMinimized ? ' minimized' : ''}${isResizing ? ' resizing' : ''}${isCollapsed ? ' collapsed' : ''}`;
    if (isDraggingPanel && dragPos && dragOffset && dragPanelSize) {
        panelClasses += ' dragging';
        panelStyle = {
            left: dragPos.x - dragOffset.x,
            top: dragPos.y - dragOffset.y,
            width: dragPanelSize.width,
            height: dragPanelSize.height,
        };
    } else {
        panelClasses += ` position-${panelPosition}`;
        if (isHorizontal) {
            panelStyle = isMinimized
                ? { height: `${collapsedHeight}px` }
                : isCollapsed
                  ? { height: `${collapsedHeight}px` }
                  : { height: `${height}px` };
        } else {
            panelStyle = isMinimized
                ? { width: `${collapsedWidth}px` }
                : isCollapsed
                  ? { width: `${collapsedWidth}px` }
                  : { width: `${width}px` };
        }
    }
    return (
        <TooltipProvider>
            {isDraggingPanel && snapOverlay}
            {/* --- Panel drag ghost (HTML snapshot) --- */}
            {isDraggingPanel &&
                panelDragGhostHtml &&
                dragPos &&
                dragOffset &&
                dragPanelSize && (
                    <div
                        className="side-panel-drag-ghost-html"
                        style={{
                            left: dragPos.x - dragOffset.x,
                            top: dragPos.y - dragOffset.y,
                            width: dragPanelSize.width,
                            height: dragPanelSize.height,
                        }}
                        dangerouslySetInnerHTML={{ __html: panelDragGhostHtml }}
                    />
                )}
            {/* --- Tab drag ghost (icon) --- */}
            {tabDragGhost && (
                <div
                    className="unified-sidepanel-icon-ghost"
                    style={{
                        left: `${tabDragGhost.x}px`,
                        top: `${tabDragGhost.y}px`,
                        transform: 'translate(-50%, -50%)',
                        position: 'fixed',
                        pointerEvents: 'none',
                        zIndex: 10000,
                    }}
                >
                    {tabDragGhost.icon}
                </div>
            )}
            <div
                key={isMinimized ? 'minimized' : 'regular'}
                ref={panelRef}
                className={panelClasses}
                style={panelStyle}
                onMouseDown={handlePanelLongPressMouseDown}
                onContextMenu={showIconAlignmentMenu}
                data-position={panelPosition}
                data-panel-id={panelId}
                data-dock-entry={panelId}
                data-dock-side={panelPosition}
                data-dock-index={dockIndex}
                data-side-panel-type={isMinimized ? 'minimized' : 'regular'}
            >
                {/* Native resize handle */}
                {panelPosition === 'left' && (
                    <div
                        className="panel-resize-handle panel-resize-handle-e"
                        onMouseDown={createPanelResizeHandleMouseDown(
                            'left',
                            panelRef,
                        )}
                    />
                )}
                {panelPosition === 'right' && (
                    <div
                        className="panel-resize-handle panel-resize-handle-w"
                        onMouseDown={createPanelResizeHandleMouseDown(
                            'right',
                            panelRef,
                        )}
                    />
                )}
                {panelPosition === 'top' && (
                    <div
                        className="panel-resize-handle panel-resize-handle-s"
                        onMouseDown={createPanelResizeHandleMouseDown(
                            'top',
                            panelRef,
                        )}
                    />
                )}
                {panelPosition === 'bottom' && (
                    <div
                        className="panel-resize-handle panel-resize-handle-n"
                        onMouseDown={createPanelResizeHandleMouseDown(
                            'bottom',
                            panelRef,
                        )}
                    />
                )}
                {/* --- HEADER: yatay panelde sola sabit, butonlar alt alta, ikon yok --- */}
                {isHorizontal ? (
                    <div className="side-panel-header horizontal-header">
                        <div className="side-panel-header-buttons-vertical">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="side-panel-header-btn"
                                        onClick={toggleMinimized}
                                    >
                                        {panelPosition === 'top' ? (
                                            isMinimized ? (
                                                <ChevronDown size={14} />
                                            ) : (
                                                <ChevronUp size={14} />
                                            )
                                        ) : isMinimized ? (
                                            <ChevronUp size={14} />
                                        ) : (
                                            <ChevronDown size={14} />
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="right"
                                    className="tooltip-content"
                                >
                                    {isMinimized ? 'Restore' : 'Minimize'}
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="side-panel-header-btn"
                                        onClick={() => setShowPanel(false)}
                                    >
                                        <CloseIcon size={14} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="right"
                                    className="tooltip-content"
                                >
                                    Close
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </div>
                ) : (
                    <div
                        className="side-panel-header"
                        onDoubleClick={toggleCollapse}
                    >
                        {!isMinimized && (
                            <span className="side-panel-header-icon">
                                <LayoutPanelLeft size={14} />
                            </span>
                        )}
                        <span className="side-panel-header-actions">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="side-panel-header-btn"
                                        onClick={toggleMinimized}
                                    >
                                        {isMinimized ? (
                                            <ChevronLeft size={14} />
                                        ) : (
                                            <ChevronRight size={14} />
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="right"
                                    className="tooltip-content"
                                >
                                    {isMinimized ? 'Restore' : 'Minimize'}
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="side-panel-header-btn"
                                        onClick={() => setShowPanel(false)}
                                    >
                                        <CloseIcon size={14} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="right"
                                    className="tooltip-content"
                                >
                                    Close
                                </TooltipContent>
                            </Tooltip>
                        </span>
                    </div>
                )}
                <div className="panel-content">
                    {isMinimized ? (
                        <MinimizedSidePanel
                            panelId={panelId}
                            visibleGroups={visibleGroups}
                            popupTab={popupTab}
                            setPopupTab={setPopupTab}
                            popupPosition={popupPosition}
                            setPopupPosition={setPopupPosition}
                            popupWidth={popupWidth}
                            setPopupWidth={setPopupWidth}
                            popupHeights={popupHeights}
                            setPopupHeights={setPopupHeights}
                            popupResizeHandle={popupResizeHandle}
                            setPopupResizeHandle={setPopupResizeHandle}
                            panelRef={panelRef}
                            onFloatingWindowDrop={undefined}
                            iconAlignment={iconAlignment}
                            onTabDragStart={(tab, groupId) => {
                                setDraggedTab({ tabId: tab.id, groupId });
                                setIsDraggingOutside(true);
                            }}
                            isDraggingPanel={isDraggingPanel}
                            dragOffset={dragOffset}
                            dragPanelSize={dragPanelSize}
                            dragPos={dragPos}
                            snapTarget={snapTarget}
                            panelPosition={panelPosition}
                            setFloatingWindows={setFloatingWindows}
                            floatWindowMinWidth={FLOAT_WINDOW_MIN_WIDTH}
                            floatWindowMinHeight={FLOAT_WINDOW_MIN_HEIGHT}
                            setPanelGroups={setPanelGroups}
                            setIsDraggingOutside={setIsDraggingOutside}
                            onPanelLongPressMouseDown={
                                handlePanelLongPressMouseDown
                            }
                            onEmpty={() => setShowPanel(false)}
                        />
                    ) : (
                        <RegularSidePanel
                            isHorizontal={isHorizontal}
                            visibleGroups={visibleGroups}
                            activeDropZone={activeDropZone}
                            handleDropZoneDragOver={handleDropZoneDragOver}
                            handleDropZoneDragLeave={handleDropZoneDragLeave}
                            handleDropOnZone={handleDropOnZone}
                            handleGroupResize={(
                                groupId: string,
                                currentHeight: number,
                            ) =>
                                createGroupResizeHandleMouseDown(
                                    groupId,
                                    currentHeight,
                                    panelRef,
                                )
                            }
                            handleHorizontalGroupResize={
                                isHorizontal
                                    ? handleHorizontalGroupResizeMouseDown
                                    : undefined
                            }
                            getPanelGroupStyle={getPanelGroupStyle}
                            toggleGroupMinimize={
                                autoCollapse.handleGroupHeaderActivate
                            }
                            autoCollapseContainerRef={autoCollapse.containerRef}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            tabDropPosition={tabDropPosition}
                            handleCloseTab={handleCloseTab}
                            panelId={panelId}
                            handleTabMouseDown={handleTabMouseDown}
                        />
                    )}
                </div>
            </div>
            {/* --- Floating windows --- */}
            {/* Rendered once by App; per-instance rendering here duplicated every floating window */}
        </TooltipProvider>
    );
};export type { PanelGroup, TabConfig };
export { DropZone };
